import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api, rupee } from "@/lib/api";
import { toast } from "sonner";
import { Check, Camera } from "lucide-react";
import CameraCapture from "@/components/CameraCapture";

const STEPS = ["Contact", "Shipping", "Photo", "Payment"];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [addr, setAddr] = useState({ line1: "", line2: "", city: "", state: "", pincode: "", country: "India" });
  const [photo, setPhoto] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [consent, setConsent] = useState(false);
  const [payment, setPayment] = useState("razorpay");
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [kyc, setKyc] = useState({ done: false, otp: "", phoneVerified: false, aadhaar: "", emi_months: 6 });
  const [busy, setBusy] = useState(false);
  const [payerUpi, setPayerUpi] = useState("");
  const [rzpReady, setRzpReady] = useState(false);
  useEffect(() => { api.get("/payments/razorpay/status").then((r) => setRzpReady(!!r.data.configured)).catch(() => {}); }, []);
  const [shipCfg, setShipCfg] = useState({ shipping_fee: 250, free_shipping_threshold: 15000 });
  useEffect(() => { api.get("/settings/shipping").then((r) => setShipCfg(r.data)).catch(() => {}); }, []);

  const tax = Math.round((subtotal - discount) * 0.05);
  const shipping = subtotal - discount >= shipCfg.free_shipping_threshold ? 0 : shipCfg.shipping_fee;
  const total = subtotal - discount + tax + shipping;
  const isEmi = payment === "emi";
  const isUpi = ["razorpay", "upi", "gpay", "phonepe", "paytm", "card"].includes(payment);
  const dp = Math.round(total * 0.2);
  const [upiIntent, setUpiIntent] = useState(null);
  useEffect(() => {
    if (isUpi || (isEmi && kyc.done)) {
      const amt = isEmi ? dp : total;
      api.get(`/payments/upi-qr?amount=${amt}&order_note=${encodeURIComponent(isEmi ? "LEE BAG EMI Down Payment" : "LEE BAG Order")}`).then((r) => setUpiIntent(r.data)).catch(() => {});
    } else setUpiIntent(null);
  }, [payment, isEmi, kyc.done, total]);

  if (items.length === 0) return <div className="text-center py-24"><p className="text-[#8C867D]">Your bag is empty.</p></div>;

  const applyCoupon = async () => {
    try {
      const { data } = await api.get(`/coupons/${coupon}`);
      const d = data.type === "percent" ? Math.min(subtotal * data.value / 100, data.max_discount || Infinity) : data.value;
      setDiscount(Math.round(d));
      toast.success("Coupon applied");
    } catch { toast.error("Invalid coupon"); }
  };

  const requestOtp = async () => {
    try {
      const { data } = await api.post("/kyc/otp/request", { phone: contact.phone });
      setKyc((k) => ({ ...k, phoneDemo: data.demo_otp, otp: "" }));
      toast.info(`DEMO SMS — OTP: ${data.demo_otp}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send OTP"); }
  };
  const verifyOtp = async () => {
    try { await api.post("/kyc/otp/verify", { phone: contact.phone, otp: kyc.otp }); setKyc({ ...kyc, phoneVerified: true }); toast.success("Phone verified"); }
    catch (e) { toast.error(e.response?.data?.detail || "Invalid OTP"); }
  };
  const sendAadhaarOtp = async () => {
    try {
      const { data } = await api.post("/kyc/aadhaar/otp", { aadhaar_last4: kyc.aadhaar });
      setKyc((k) => ({ ...k, aadhaarDemo: data.demo_otp, aadhaarOtp: "" }));
      toast.info(`DEMO Aadhaar OTP: ${data.demo_otp}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send Aadhaar OTP"); }
  };
  const doAadhaar = async () => {
    try {
      const r = await api.post("/kyc/aadhaar/verify", { aadhaar_last4: kyc.aadhaar, otp: kyc.aadhaarOtp });
      if (r.data.success) { setKyc({ ...kyc, done: true }); toast.success("Aadhaar verified"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Aadhaar verification failed"); }
  };

  const place = async () => {
    setBusy(true);
    try {
      const r = await api.post("/orders", {
        items, subtotal, tax, shipping, discount, total, coupon_code: coupon || null,
        payment_method: payment, payer_upi: payerUpi || null, shipping_address: { ...addr, ...contact }, billing_address: null,
        customer_photo: photo, consent_photo_public: consent,
        kyc: isEmi ? { verified: true, mocked: true, aadhaar_last4: kyc.aadhaar.slice(-4) } : null,
        emi_plan: isEmi ? { months: kyc.emi_months, monthly: Math.round((total - dp) / kyc.emi_months), down_payment: dp, down_payment_paid: true } : null,
      });
      clear();
      if (rzpReady && !isEmi) {
        try {
          const { data } = await api.post("/payments/razorpay/link", { order_id: r.data.order_id });
          toast.success("Redirecting to secure Razorpay payment…");
          window.location.href = data.url;
          return;
        } catch { /* fall through to confirmation page */ }
      }
      toast.success(isUpi || isEmi ? "Order received — confirmed after payment verification" : "Order placed!");
      nav(`/order/${r.data.order_id}`);
    } catch (er) { toast.error(er.response?.data?.detail || "Order failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 fade-up">
      <h1 className="font-serif text-3xl md:text-5xl text-center">Checkout</h1>
      <div className="flex items-center justify-center gap-2 mt-6 mb-12 text-xs uppercase tracking-widest">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${i <= step ? "bg-[#121212] text-[#FBF9F5]" : "bg-[#E2DDD5] text-[#8C867D]"}`}>{i < step ? <Check size={12} /> : i + 1}</span>
            <span className={i <= step ? "" : "text-[#8C867D]"}>{s}</span>
            {i < STEPS.length - 1 && <span className="w-8 h-px bg-[#E2DDD5]" />}
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-[1fr_380px] gap-12">
        <div>
          {step === 0 && (
            <div className="space-y-4">
              <h3 className="font-serif text-2xl">Contact Information</h3>
              {["name", "email", "phone"].map((k) => (
                <input key={k} placeholder={k[0].toUpperCase() + k.slice(1)} value={contact[k]} onChange={(e) => setContact({ ...contact, [k]: e.target.value })} className="w-full border border-[#E2DDD5] px-4 py-3 text-sm" data-testid={`checkout-contact-${k}`} />
              ))}
              <button onClick={() => setStep(1)} disabled={!contact.name || !contact.email} className="btn-lux" data-testid="checkout-next-1">Continue to Shipping</button>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-serif text-2xl">Shipping Address</h3>
              {[["line1","Address Line 1"],["line2","Line 2 (optional)"],["city","City"],["state","State"],["pincode","PIN Code"]].map(([k,l]) => (
                <input key={k} placeholder={l} value={addr[k]} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} className="w-full border border-[#E2DDD5] px-4 py-3 text-sm" data-testid={`checkout-addr-${k}`} />
              ))}
              <div className="flex gap-3">
                <button onClick={() => setStep(0)} className="btn-lux-outline">Back</button>
                <button onClick={() => setStep(2)} disabled={!addr.line1 || !addr.city || !addr.pincode} className="btn-lux flex-1" data-testid="checkout-next-2">Continue</button>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-serif text-2xl">Owner Photo (Optional)</h3>
              <p className="text-sm text-[#55524D]">Attach your photo to your LEE BAG digital certificate. Shown on QR verification page only if you consent.</p>
              <div className="flex gap-4 items-start">
                <button onClick={() => setCamOpen(true)} className="btn-lux-outline flex items-center gap-2" data-testid="checkout-photo-camera"><Camera size={16} /> Take Live Photo</button>
                <label className="btn-lux-outline cursor-pointer" data-testid="checkout-photo-upload">Upload
                  <input type="file" accept="image/*" hidden onChange={(e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const r = new FileReader(); r.onload = () => setPhoto(r.result); r.readAsDataURL(f);
                  }} />
                </label>
              </div>
              {photo && <img src={photo} alt="you" className="w-32 h-32 object-cover border border-[#E2DDD5]" />}
              <label className="flex items-center gap-2 text-sm mt-2">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="checkout-photo-consent" /> I consent to displaying my photo on the public QR verification page.
              </label>
              {camOpen && <CameraCapture onCapture={(d) => { setPhoto(d); setCamOpen(false); toast.success("Photo captured"); }} onClose={() => setCamOpen(false)} />}
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="btn-lux-outline">Back</button>
                <button onClick={() => { if (consent && !photo) { toast.error("Please provide your photo, or untick the photo consent box"); return; } setStep(3); }} className="btn-lux flex-1" data-testid="checkout-next-3">Continue to Payment</button>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-serif text-2xl">Payment</h3>
              <div className="grid grid-cols-2 gap-3">
                {[["razorpay","Razorpay (All-in-one)"],["upi","UPI"],["gpay","Google Pay"],["phonepe","PhonePe"],["paytm","Paytm"],["card","Card"],["netbanking","Net Banking"],["wallet","Wallet"],["emi","No-Cost EMI"]].map(([v, l]) => (
                  <button key={v} onClick={() => setPayment(v)} className={`p-4 text-sm text-left border ${payment === v ? "border-[#121212] bg-[#F5F2EB]" : "border-[#E2DDD5]"}`} data-testid={`checkout-pay-${v}`}>{l}</button>
                ))}
              </div>
              {isUpi && upiIntent && (
                <div className="border border-[#C5A059] p-4 mt-3 space-y-3" data-testid="upi-pay-block">
                  <p className="eyebrow">Pay To (LEE BAG)</p>
                  <p className="font-mono text-sm" data-testid="upi-receiver-id">{upiIntent.upi_id}</p>
                  <p className="text-sm">Amount: <span className="font-medium">{rupee(upiIntent.amount)}</span></p>
                  <div className="flex flex-col items-center gap-2 bg-white p-4 border border-[#E2DDD5]">
                    <img src={upiIntent.qr_image} alt="Payment QR" className="w-44 h-44" data-testid="upi-qr-image" />
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C867D]">Scan with any UPI app</p>
                  </div>
                  <a href={upiIntent.upi_url} className="btn-lux-gold inline-flex" data-testid="upi-pay-button">Open UPI App & Pay</a>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#8C867D] mb-1">Your UPI ID (optional — we can send a collect request)</label>
                    <input placeholder="e.g. yourname@okhdfc" value={payerUpi} onChange={(e) => setPayerUpi(e.target.value)} className="border border-[#E2DDD5] px-3 py-2 text-sm w-full" data-testid="checkout-payer-upi" />
                  </div>
                  <p className="text-xs text-[#8C867D]">Opens GPay, PhonePe, Paytm or any UPI app on your mobile with amount pre-filled.</p>
                  <p className="text-xs text-[#8C867D]">Order is confirmed automatically once payment is received (until the payment gateway is activated, LEE BAG verifies payments within a few minutes).</p>
                </div>
              )}
              {["netbanking", "wallet"].includes(payment) && (
                <div className="border border-[#E2DDD5] p-4 mt-3 text-sm text-[#55524D]" data-testid="manual-pay-note">
                  Pay via {payment === "netbanking" ? "Net Banking" : "Wallet"} transfer to LEE BAG UPI <span className="font-mono">sarbjsarbjkaur@oksbi</span> — your order is confirmed within minutes of payment verification.
                </div>
              )}
              {isEmi && (
                <div className="border border-[#C5A059] bg-[#FBF9F5] p-4 space-y-3 mt-3">
                  <p className="eyebrow">KYC Verification</p>
                  {!kyc.phoneVerified ? (
                    <div className="space-y-2">
                      <button onClick={requestOtp} className="btn-lux-outline !py-2 !px-4" data-testid="kyc-otp-send">Send OTP to {contact.phone || "phone"}</button>
                      {kyc.phoneDemo && <p className="text-xs text-[#997A31]" data-testid="kyc-otp-demo">DEMO SMS (gateway pending): OTP is <span className="font-mono font-medium">{kyc.phoneDemo}</span></p>}
                      <div className="flex gap-2">
                        <input placeholder="Enter 6-digit OTP" value={kyc.otp} onChange={(e) => setKyc({ ...kyc, otp: e.target.value })} className="border border-[#E2DDD5] px-3 py-2 text-sm flex-1" data-testid="kyc-otp-input" />
                        <button onClick={verifyOtp} className="btn-lux !py-2 !px-4" data-testid="kyc-otp-verify">Verify</button>
                      </div>
                    </div>
                  ) : !kyc.done ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input placeholder="Aadhaar last 4 digits" maxLength={4} value={kyc.aadhaar} onChange={(e) => setKyc({ ...kyc, aadhaar: e.target.value })} className="border border-[#E2DDD5] px-3 py-2 text-sm flex-1" data-testid="kyc-aadhaar" />
                        <button onClick={sendAadhaarOtp} className="btn-lux-outline !py-2 !px-4" data-testid="kyc-aadhaar-send">Send OTP</button>
                      </div>
                      {kyc.aadhaarDemo && <p className="text-xs text-[#997A31]" data-testid="kyc-aadhaar-demo">DEMO Aadhaar OTP: <span className="font-mono font-medium">{kyc.aadhaarDemo}</span></p>}
                      <div className="flex gap-2">
                        <input placeholder="Enter Aadhaar OTP" value={kyc.aadhaarOtp || ""} onChange={(e) => setKyc({ ...kyc, aadhaarOtp: e.target.value })} className="border border-[#E2DDD5] px-3 py-2 text-sm flex-1" data-testid="kyc-aadhaar-otp" />
                        <button onClick={doAadhaar} className="btn-lux !py-2 !px-4" data-testid="kyc-aadhaar-verify">Verify Aadhaar</button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="text-sm text-[#5B1C22]">✓ KYC Approved (MOCKED). Select tenure:
                        <select value={kyc.emi_months} onChange={(e) => setKyc({ ...kyc, emi_months: +e.target.value })} className="ml-2 border border-[#E2DDD5] px-2 py-1" data-testid="kyc-emi-months">
                          {[3, 6, 9, 12].map((m) => <option key={m} value={m}>{m} months · {rupee(Math.round((total - dp)/m))}/mo</option>)}
                        </select>
                      </div>
                      <div className="border-t border-[#E2DDD5] pt-3 space-y-2">
                        <p className="text-xs uppercase tracking-widest">Down Payment (20%) — required before confirming order</p>
                        {upiIntent && (
                          <>
                            <p className="text-sm">Pay <span className="font-medium" data-testid="emi-dp-amount">{rupee(dp)}</span> to <span className="font-mono">{upiIntent.upi_id}</span></p>
                            <div className="flex flex-col items-center gap-2 bg-white p-4 border border-[#E2DDD5]">
                              <img src={upiIntent.qr_image} alt="Down payment QR" className="w-40 h-40" data-testid="emi-dp-qr-image" />
                              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C867D]">Scan with any UPI app</p>
                            </div>
                            <a href={upiIntent.upi_url} className="btn-lux-gold inline-flex" data-testid="emi-dp-pay-button">Pay Down Payment via UPI</a>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-3 mt-4">
                <button onClick={() => setStep(2)} className="btn-lux-outline">Back</button>
                <button onClick={place} disabled={busy || (isEmi && !kyc.done)} className="btn-lux flex-1" data-testid="checkout-place-order">{busy ? "…" : "Place Order"}</button>
              </div>
              <p className="text-[10px] text-[#8C867D] mt-2">KYC OTPs are demo-delivered on screen (SMS/e-KYC provider integration pending). Payments are verified manually by LEE BAG.</p>
            </div>
          )}
        </div>
        <aside className="bg-[#F5F2EB] p-8 h-fit sticky top-24">
          <h4 className="eyebrow mb-4">Order Summary</h4>
          {items.map((i) => (
            <div key={i.product_id} className="flex gap-3 mb-4 text-sm">
              <img src={i.image} className="w-16 h-20 object-cover" alt="" />
              <div className="flex-1"><p>{i.name}</p><p className="text-xs text-[#8C867D]">Qty {i.quantity}</p></div>
              <span>{rupee(i.price * i.quantity)}</span>
            </div>
          ))}
          <div className="border-t border-[#E2DDD5] pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{rupee(subtotal)}</span></div>
            <div className="flex gap-2">
              <input placeholder="Coupon (LUXURY10)" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} className="border border-[#E2DDD5] px-2 py-1 text-xs flex-1" data-testid="checkout-coupon" />
              <button onClick={applyCoupon} className="text-xs uppercase tracking-widest" data-testid="checkout-coupon-apply">Apply</button>
            </div>
            {discount > 0 && <div className="flex justify-between text-[#5B1C22]"><span>Discount</span><span>−{rupee(discount)}</span></div>}
            <div className="flex justify-between"><span>Tax (5%)</span><span>{rupee(tax)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : rupee(shipping)}</span></div>
            <div className="flex justify-between text-base font-medium border-t border-[#E2DDD5] pt-2 mt-2"><span>Total</span><span>{rupee(total)}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
